import { inject, Injectable } from '@angular/core';

import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { PendoService } from '@ciphr/core/pendo';

import { accountApiActions } from './actions/account-api.actions';
import { accountFeatureActions } from './actions/account-feature.actions';
import { AccountService } from '../../account.service';
import { DocumentService } from '../../core/document.service';
import { SSO_PRODUCT_VERSION } from '../../core/product-version.token';

@Injectable({ providedIn: 'root' })
export class AccountEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  private readonly accountService = inject(AccountService);
  private readonly documentService = inject(DocumentService);
  private readonly pendoService = inject(PendoService);

  private readonly productVersion = inject(SSO_PRODUCT_VERSION);

  loadAccountInformation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(accountFeatureActions.loadAccountInformation),
      switchMap(() =>
        this.accountService.checkAuth().pipe(
          tap(({ tenantId, orgName, userName, id, role }) =>
            this.pendoService.initialize({ productVersion: this.productVersion, tenantId, orgName, userName, id, ssoRole: role }),
          ),
          map((account) => accountApiActions.loadedAccountInformationSuccessfully({ account })),
          catchError(() => of(accountApiActions.loadedAccountInformationFailed())),
        ),
      ),
    );
  });

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(accountFeatureActions.logout),
      mergeMap(() => this.accountService.signOut().pipe(map(({ redirectUrl }) => accountApiActions.logoutSuccessfully({ redirectUrl })))),
    ),
  );

  redirectToLogoutAfterSuccessfullyLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(accountApiActions.logoutSuccessfully),
        tap(() => this.pendoService.clearSession()),
        tap(() => this.store.dispatch(accountFeatureActions.clearState())),
        tap(({ redirectUrl }) => this.documentService.redirectLocationHref(redirectUrl)),
      ),
    { dispatch: false },
  );
}
